import { useTranslation } from 'react-i18next';
import { Flex, Skeleton, spaces, Text, ColorsV2 } from '@electricjs/arc';

import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { SettingValueResolver } from '@/components/Applications/ApplicationDetails/Settings/SettingValueResolver';
import { useResolvedRowSettings } from './useResolvedRowSettings';

type ManagedAppSettingsPanelProps = {
  applicationKey: string;
  /** All of the groups this employee is being onboarded into. */
  groupIds: string[];
};

export const ManagedAppSettingsPanel = ({
  applicationKey,
  groupIds,
}: ManagedAppSettingsPanelProps) => {
  const { t } = useTranslation('common');
  const { t: tApplications } = useTranslation('applications');
  const organizationId = useGetOrganizationId();
  const { space_m_rem, space_s_rem, space_3xs_rem } = spaces;

  const { settings, isLoading, isError } = useResolvedRowSettings({
    organizationId,
    applicationKey,
    groupIds,
  });

  const settingEntries = Object.entries(settings);

  return (
    <Flex
      flexDirection="column"
      width="100%"
      py={space_s_rem}
      px={space_s_rem}
      gap={space_s_rem}>
      <Text variant="body-2" intent="secondary">
        {t('onboardings.onboardEmployeeModalApps.accessAndSettingsHeader')}
      </Text>

      <Flex flexDirection="column" gap={space_s_rem}>
        {isLoading && <Skeleton width="20rem" height="4rem" />}

        {!isLoading && isError && (
          <Text variant="body-2" intent="danger">
            {t('errors.somethingWentWrong')}
          </Text>
        )}

        {!isLoading && !isError && settingEntries.length === 0 && (
          <Text
            variant="body-2"
            intent="secondary"
            style={{ fontStyle: 'italic' }}>
            {t('onboardings.onboardEmployeeModalApps.noAdditionalSettings')}
          </Text>
        )}

        {!isLoading &&
          !isError &&
          settingEntries.length > 0 &&
          settingEntries.map(([key, { label, value, type }]) => {
            const isArray = Array.isArray(value);
            const configuredCountText = tApplications(
              'settingsPane.configuredCount',
              {
                count: isArray ? value.length : 1,
              }
            );

            return (
              <Flex
                key={key}
                flexDirection="column"
                gap={space_3xs_rem}
                borderLeft="1px solid"
                paddingLeft={space_m_rem}
                borderColor={ColorsV2.PRIMARY}>
                <Text variant="label-small" intent="secondary">
                  {label}
                </Text>
                <Flex alignItems="center" gap={space_3xs_rem}>
                  <SettingValueResolver
                    applicationKey={applicationKey}
                    settingName={key}
                    value={value}
                    configuredCountText={configuredCountText}
                    type={type}
                  />
                </Flex>
              </Flex>
            );
          })}
      </Flex>
    </Flex>
  );
};
